import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Field } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Save, Building2, Percent, Target, ShieldAlert } from "lucide-react";

const GROUPS = [
  {
    title: "Vincoli di Acquisizione", icon: Target,
    fields: [
      ["pilot_city", "Città pilota", "text"],
      ["max_total_cost", "Costo totale massimo (€)", "number"],
      ["target_total_cost", "Costo totale target (€)", "number"],
      ["min_profit", "Utile minimo (€)", "number"],
      ["max_duration_months", "Durata massima (mesi)", "number"],
    ],
  },
  {
    title: "Stress Test", icon: ShieldAlert,
    fields: [
      ["sale_stress_pct", "Stress prezzo di vendita (%)", "number"],
      ["renovation_stress_pct", "Stress costi ristrutturazione (%)", "number"],
      ["unexpected_costs_pct", "Costi imprevisti ristrutturazione (%)", "number"],
    ],
  },
  {
    title: "Costi Operativi (assunzioni)", icon: Percent,
    fields: [
      ["purchase_tax_pct", "Imposte di acquisto (%)", "number"],
      ["notary_fee", "Spese notarili (€)", "number"],
      ["technicians_cost", "Costi tecnici (€)", "number"],
      ["holding_cost_per_month", "Costi di mantenimento / mese (€)", "number"],
      ["sale_cost_pct", "Costi di vendita (%)", "number"],
    ],
  },
  {
    title: "Dati Aziendali", icon: Building2,
    fields: [
      ["company_name", "Ragione sociale", "text"],
      ["company_city", "Città", "text"],
      ["company_email", "Email", "text"],
    ],
  },
];

export default function Settings() {
  const { isAdmin } = useAuth();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);

  const set = (k, v, type) => setS({ ...s, [k]: type === "number" ? v : v });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...s };
      delete payload.id; delete payload.updated_at;
      // coerce numbers
      GROUPS.forEach((g) => g.fields.forEach(([k, , type]) => { if (type === "number") payload[k] = Number(payload[k]) || 0; }));
      const { data } = await api.put("/settings", payload);
      setS(data);
      toast.success("Impostazioni salvate");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  if (!s) return <div className="py-16 text-center text-muted-foreground">Caricamento...</div>;

  return (
    <div data-testid="settings-page">
      <PageHeader title="Impostazioni Aziendali" subtitle="Parametri di business per la valutazione di acquisizione">
        {isAdmin && <Button onClick={save} disabled={saving} data-testid="save-settings-btn"><Save className="h-4 w-4 mr-2" />{saving ? "Salvataggio..." : "Salva"}</Button>}
      </PageHeader>

      {!isAdmin && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800 mb-6">
          Solo gli amministratori possono modificare le impostazioni aziendali. Visualizzazione in sola lettura.
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {GROUPS.map((g) => (
          <Card key={g.title} className="p-6">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><g.icon className="h-4 w-4 text-primary" />{g.title}</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {g.fields.map(([k, label, type]) => (
                <Field key={k} label={label} className={type === "text" && k.includes("company") ? "sm:col-span-2" : ""}>
                  <Input type={type} value={s[k] ?? ""} disabled={!isAdmin} onChange={(e) => set(k, e.target.value, type)} data-testid={`setting-${k}`} />
                </Field>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
